/**
 * Supabase Client Module
 * Handles Supabase client initialization and configuration
 */

import { createClient, SupabaseClient as SupabaseClientType, SupabaseClientOptions } from '@supabase/supabase-js';
import { config, isServiceConfigured } from '@/shared/config/environment';

class SupabaseClientWrapper {
  private client: SupabaseClientType | null = null;
  private firebaseTokenGetter: (() => Promise<string | null>) | null = null;
  private firebaseUidGetter: (() => Promise<string | null>) | null = null;
  private cachedFirebaseUid: string | null = null;

  constructor() {
    this.initializeSupabase();
  }

  private initializeSupabase() {
    if (!isServiceConfigured('supabase')) {
      console.warn('Supabase is not configured. Database features will be limited.');
      return;
    }

    try {
      // Initialize Supabase client with Firebase auth integration
      const options: SupabaseClientOptions<'public'> = {
        auth: {
          persistSession: false, // We'll use Firebase tokens
          autoRefreshToken: false,
          detectSessionInUrl: false
        },
        realtime: {
          params: {
            eventsPerSecond: 10
          }
        },
        global: {
          headers: {
            'X-Client-Info': 'SwaySuite'
          }
        },
        db: {
          schema: 'public'
        }
      };

      this.client = createClient(config.supabase.url, config.supabase.anonKey, options);

      console.log('✅ Supabase client initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize Supabase client:', error);
    }
  }

  /**
   * Set Firebase token getter for authentication
   * @param tokenGetter Function that returns Firebase token
   */
  setFirebaseTokenGetter(tokenGetter: () => Promise<string | null>) {
    this.firebaseTokenGetter = tokenGetter;
    console.log('📝 Firebase token getter configured for future custom auth integration');
  }

  /**
   * Set Firebase UID getter for RLS policies
   * @param uidGetter Function that returns Firebase UID
   */
  async setFirebaseUidGetter(uidGetter: () => Promise<string | null>) {
    this.firebaseUidGetter = uidGetter;
    // Pre-fetch and cache the Firebase UID
    await this.refreshFirebaseUid();
    console.log('📝 Firebase UID getter configured. Cached UID:', this.cachedFirebaseUid);
  }

  /**
   * Refresh the cached Firebase UID
   */
  private async refreshFirebaseUid() {
    if (this.firebaseUidGetter) {
      this.cachedFirebaseUid = await this.firebaseUidGetter();
    }
  }

  /**
   * Get client instance with Firebase UID header
   * @returns SupabaseClient instance with headers applied
   */
  getClient(): SupabaseClientType {
    if (!this.client) {
      throw new Error('Supabase client is not initialized');
    }
    return this.client;
  }

  /**
   * Execute a query with Firebase UID header
   * This method adds the firebase-uid header to the request
   */
  async queryWithAuth<T>(queryFn: (client: SupabaseClientType) => Promise<T>): Promise<T> {
    // Refresh Firebase UID before query
    await this.refreshFirebaseUid();

    if (!this.cachedFirebaseUid) {
      console.error('❌ No Firebase UID available. Cannot execute authenticated query.');
      throw new Error('User not authenticated - no Firebase UID available');
    }

    console.log('🔑 Executing query with Firebase UID:', this.cachedFirebaseUid);

    // Use the client directly - the fetch wrapper should add the header
    return queryFn(this.client);
  }

  /**
   * Check if client is initialized
   * @returns boolean indicating if client is ready
   */
  isInitialized(): boolean {
    return this.client !== null;
  }
}

// Export singleton instance
export const supabaseClient = new SupabaseClientWrapper();
export { SupabaseClientWrapper };